// Synth keys — polyphonic triangle synth, played by scale degrees (air-keys).
// Macro = post-filter cutoff (brightness) from the left hand.

import * as Tone from "tone";
import { degreeToNote } from "../util/scales.ts";
import type { NoteEvent, ScaleName } from "../util/types.ts";
import { clampVelocity, macroToCutoff, type Instrument } from "./types.ts";

const BASE_OCTAVE = 4;
const DEFAULT_MACRO = 0.8;

export function createSynthKeys(): Instrument {
  let synth: Tone.PolySynth<Tone.Synth> | null = null;
  let filter: Tone.Filter | null = null;
  let key = "C";
  let scale: ScaleName = "majorPentatonic";

  const resolve = (ev: NoteEvent): string | number =>
    ev.degree !== undefined ? degreeToNote(ev.degree, key, scale, BASE_OCTAVE) : (ev.note ?? "C4");

  return {
    id: "synth-keys",
    name: "Synth keys",
    category: "keys",
    archetype: "air-keys",
    scaleLocked: true,
    polyphony: "poly",
    init(output) {
      filter = new Tone.Filter({ type: "lowpass", frequency: macroToCutoff(DEFAULT_MACRO), Q: 0.7 });
      filter.connect(output);
      synth = new Tone.PolySynth(Tone.Synth);
      synth.set({
        oscillator: { type: "triangle" },
        envelope: { attack: 0.005, decay: 0.18, sustain: 0.25, release: 0.5 },
      });
      synth.connect(filter);
    },
    trigger(ev, time) {
      synth?.triggerAttackRelease(resolve(ev), ev.duration ?? "8n", time, clampVelocity(ev.velocity));
    },
    setScale(k, s) {
      key = k;
      scale = s;
    },
    setMacro(v) {
      filter?.frequency.rampTo(macroToCutoff(v), 0.05);
    },
    dispose() {
      synth?.dispose();
      filter?.dispose();
      synth = null;
      filter = null;
    },
  };
}
